import type { ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import { readonly, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWorkspaceSchemaStore } from '#layers/data-model/stores/workspace-schema';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';

interface UseRemoveDataSourceOptions {
	/**
	 * Runs after the removal succeeded but before the schema refresh drops the
	 * source, so callers rendered off that source can navigate away first.
	 */
	onRemoved?: () => void | Promise<void>;
}

/**
 * Deletes a data source and refreshes the workspace schema, surfacing success or
 * failure as a toast.
 */
export function useRemoveDataSource(options: UseRemoveDataSourceOptions = {}) {
	const { t } = useI18n();
	const { refresh: refreshSchema } = useWorkspaceSchemaStore();
	const { onSuccess, onError } = useToastResultHandling({
		success: { title: t('pages.data_model.remove_source_success.title') },
		error: { title: t('pages.data_model.remove_source_error.title') },
	});

	const isRemoving = ref(false);

	async function removeDataSource(source: ResolvedMonospaceDataSource) {
		const handle = useWorkspaceRuntimeHandle();
		const { fetch } = handle;

		isRemoving.value = true;

		return handle.run(async () => {
			try {
				await fetch(`sources/data/${source.id}`, { method: 'DELETE' });

				if (!handle.isActive()) return;

				await options.onRemoved?.();

				if (!handle.isActive()) return;

				onSuccess();
				await refreshSchema();
			}
			catch (error) {
				if (handle.isActive()) onError(error);
			}
			finally {
				isRemoving.value = false;
			}
		});
	}

	return { removeDataSource, isRemoving: readonly(isRemoving) };
}
